import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { TranslatePipe } from '../pipes/translate.pipe';
import { PlatformService } from '../services/platform.service';

/**
 * Cookie / tracking consent banner (the woman's first-load opt-in "mark").
 *
 * It must be the FIRST thing shown, because GTM / Google Ads / FullStory / HEAP would
 * otherwise track her on the landing + signup pages, long before she reaches the scan
 * consent form. The same choice also appears as a clause in that consent form; both
 * call `window.applyTrackingConsent('granted'|'denied')` (see the bootstrap in index.html).
 *
 * Web only, and only until a choice is stored (localStorage `cookieConsent`).
 */
@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, MatDialogModule],
  templateUrl: './cookie-consent.component.html',
  styleUrl: './cookie-consent.component.scss',
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  visible = false;
  // B4: the banner is `position:fixed; z-index:11000`, so while a modal consent
  // window (the camera/scan consent dialog) is open it would float on top of it.
  // Hide the banner whenever a dialog is open, and re-read the stored choice when
  // it closes: the consent form carries its own tracking clause, so submitting it
  // already recorded the cookie decision and the banner must stay gone.
  dialogOpen = false;
  // Staff screens (system admin, clinic admin) are not the woman-facing app:
  // no cookie banner there.
  onAdmin = false;
  private subs: Subscription[] = [];

  constructor(
    private platform: PlatformService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Native apps do not load the web tags; nothing to gate there.
    if (!this.platform.isWeb) return;
    window.addEventListener(CookieConsentComponent.REOPEN_EVENT, this.reopen);
    this.refreshVisible();
    this.dialogOpen = this.dialog.openDialogs.length > 0;
    this.onAdmin = isAdminUrl(this.router.url);
    this.subs.push(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e) => (this.onAdmin = isAdminUrl((e as NavigationEnd).urlAfterRedirects))),
      this.dialog.afterOpened.subscribe(() => (this.dialogOpen = true)),
      this.dialog.afterAllClosed.subscribe(() => {
        this.dialogOpen = false;
        // The consent form may have just recorded the tracking (cookie) choice.
        this.refreshVisible();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    window.removeEventListener(CookieConsentComponent.REOPEN_EVENT, this.reopen);
  }

  /** Fired by the profile "Cookie settings" link to show the banner again. */
  static readonly REOPEN_EVENT = 'app:open-cookie-settings';
  private reopen = (): void => {
    this.visible = true;
  };

  /** Manage panel: two separate, unticked-by-default choices. */
  managing = false;
  analytics = false;
  marketing = false;

  manage(): void {
    this.analytics = readFlag('cookieConsent');
    this.marketing = readFlag('cookieConsentMarketing');
    this.managing = true;
  }

  /** "Accept analytics": analytics only, marketing stays denied. */
  accept(): void {
    this.decide({ analytics: true, marketing: false });
  }

  decline(): void {
    this.decide({ analytics: false, marketing: false });
  }

  save(): void {
    this.decide({ analytics: this.analytics, marketing: this.marketing });
  }

  private refreshVisible(): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem('cookieConsent');
    } catch {
      stored = null;
    }
    this.visible = stored !== 'granted' && stored !== 'denied';
    if (this.visible) this.managing = false;
  }

  private decide(state: { analytics: boolean; marketing: boolean }): void {
    const apply = (window as any).applyTrackingConsent;
    if (typeof apply === 'function') {
      apply(state);
    } else {
      try {
        localStorage.setItem('cookieConsent', state.analytics ? 'granted' : 'denied');
        localStorage.setItem('cookieConsentMarketing', state.marketing ? 'granted' : 'denied');
      } catch {}
    }
    this.visible = false;
    this.managing = false;
  }
}

/** Stored consent flag; only an explicit 'granted' counts. */
export function readFlag(key: 'cookieConsent' | 'cookieConsentMarketing'): boolean {
  try {
    return localStorage.getItem(key) === 'granted';
  } catch {
    return false;
  }
}

function isAdminUrl(url: string): boolean {
  // /admin, /en/admin/..., /he/app/clinic/<slug>/admin
  return /\/admin(\/|$)/.test(String(url || '').split(/[?#]/)[0]);
}
